import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthPage, { inputClass, labelClass, primaryBtn } from "@/components/account/AuthPage";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

export default function ResetPassword() {
  const { t } = useLang();
  useSeo({ title: `${t("auth.resetTitle")} – Nivå Digital`, description: t("auth.resetSub"), noindex: true });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError(t("auth.mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      toast.success(t("auth.resetDone"));
      navigate("/logga-in");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage tag={t("nav.account")} title={t("auth.resetTitle")} sub={t("auth.resetSub")} testid="reset-page">
      <form data-testid="reset-form" onSubmit={submit} className="space-y-5">
        <div>
          <label htmlFor="reset-password" className={labelClass}>{t("auth.newPassword")}</label>
          <input id="reset-password" data-testid="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} autoComplete="new-password" />
          <p className="mt-1.5 text-xs text-fog/70">{t("auth.passwordHint")}</p>
        </div>
        <div>
          <label htmlFor="reset-confirm" className={labelClass}>{t("auth.confirmPassword")}</label>
          <input id="reset-confirm" data-testid="reset-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass} autoComplete="new-password" />
        </div>
        {error && <p data-testid="reset-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>}
        <button data-testid="reset-submit" type="submit" disabled={loading} className={primaryBtn}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.resetBtn")}
        </button>
        <p className="text-center text-sm text-fog">
          <Link data-testid="reset-login-link" to="/logga-in" className="font-semibold text-mint hover:underline">{t("auth.loginLink")}</Link>
        </p>
      </form>
    </AuthPage>
  );
}
